import React, { useState, useMemo, useEffect } from 'react';
import { Grid, IconButton, Typography, TextField, Button, Chip } from '@mui/material';
import { Send, AttachFile, DeleteOutline } from '@mui/icons-material';
import { Colors, isLightTheme } from '../config/default';
import { addComment, getOwnerUsers } from '../services/services';
import { useSelector } from 'react-redux';
import { commentsInput } from '../constants/appConstants';
import { useDropzone } from 'react-dropzone';
import CustomSelect from './customSelect';

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px dashed #bdbdbd',
  padding: '20px',
  color: '#bdbdbd',
  outline: 'none',
  cursor: 'pointer',
  fontSize: '.9rem',
  borderRadius: '10px',
  textAlign: 'center'
};


const CommentsInput = ({
  onAddComment,
  id,
  setOpenCommentModal,
  status,
  handleSubmitClick,
  text,
  parentId,
  isQuestion,
  isResolved,
  show
}) => {
  const [attachments, setAttachments] = useState([]);
  const [inputVal, setInputVal] = useState('');
  const userDetails = useSelector((state) => state?.userInfo?.userInfo);
  const [loading, setLoading] = useState(false);
  const [owners, setOwners] = React.useState([]);
  const [ownerAnchorEl, setOwnerAnchorEl] = useState(null);
  const [mentions, setMentions] = useState([]);
  const resolved = isResolved || false;
  const question = isQuestion || false;
  const userEmail = userDetails?.user?.email;

  const onDrop = (acceptedFiles) => {
    setAttachments((prevFiles) => [...prevFiles, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    multiple: true
  });

  const handleDeleteAttachment = (fileName) => {
    setAttachments((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (show !== 'resolve' && (!inputVal.trim() || (question && mentions.length === 0))) return;
    setLoading(true);

    const newComment = {
      comment: inputVal,
      time: Date.now(),
      userName: userDetails?.user?.userName,
      email: userDetails?.user?.email,
      role: userDetails?.role,
      mentions: mentions,
      parentId: parentId || null
    };

    await addComment(id, newComment, attachments, question, resolved);

    setInputVal('');
    setAttachments([]);
    if (onAddComment) {
      onAddComment();
    }
    setOpenCommentModal(false);
    if (handleSubmitClick) {
      handleSubmitClick(e, status);
    }
    setLoading(false);
  };

  const getUsers = async () => {
    const result = await getOwnerUsers();
    if (result?.status === 200) {
      const filteredUsers = result?.data?.payload?.data?.filter((item) => {
        return item?.signUp === true && item?.email !== userEmail;
      });
      const emails = filteredUsers?.map((item) => item?.email);
      setOwners(emails);
    }
  };
  useEffect(() => {
    getUsers();
  }, []);

  // Colors read at render (not module scope) so a live theme swap is reflected.
  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? { borderColor: Colors.DEEPGREY } : {}),
      ...(isDragAccept ? { borderColor: Colors.TOURQUISE } : {}),
      ...(isDragReject ? { borderColor: Colors.RED } : {})
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const handleOwnerSelect = (event, selectedOwner) => {
    if (selectedOwner === 'clear') {
      setMentions([]);
      setOwnerAnchorEl(null);
      return;
    }

    if (selectedOwner === 'backdropClick' || selectedOwner === 'escapeKeyDown') {
      setOwnerAnchorEl(null);
      return;
    }

    if (!mentions?.includes(selectedOwner)) {
      setMentions((prev) => [...prev, selectedOwner]);
    }

    setOwnerAnchorEl(null);
  };

  const handlePasteFiles = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const pastedFiles = [];
    for (let i = 0; i < items?.length; i++) {
      const item = items[i];
      if (item?.kind === 'file') {
        const file = item?.getAsFile();
        if (file) {
          pastedFiles?.push(file);
        }
      }
    }

    if (pastedFiles?.length > 0) {
      setAttachments((prev) => [...prev, ...pastedFiles]);
    }
  };
  return (
    <form onSubmit={handleSubmit} style={{ margin: 'auto' }}>
      <Typography
        variant="h6"
        sx={{ color: Colors.WHITE, marginBottom: '1em', textAlign: 'center' }}>
        {show === 'question'
          ? commentsInput.QUESTION
          : show === 'resolve'
          ? commentsInput.RESOLVE
          : commentsInput.COMMENT}
      </Typography>

      <TextField
        multiline
        fullWidth
        minRows={5}
        value={inputVal}
        onPaste={handlePasteFiles}
        onChange={(e) => setInputVal(e.target.value)}
        placeholder={show === 'question' ? commentsInput.QUESTION_MESSAGE : commentsInput.COMMENT}
        variant="standard"
        InputProps={{
          disableUnderline: true,
          style: {
            color: Colors.WHITE,
            backgroundColor: isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK,
            fontSize: '1rem',
            borderRadius: '10px'
          }
        }}
        sx={{
          '& .MuiInputBase-root': {
            borderRadius: '10px',
            overflow: 'hidden'
          },
          '& .MuiInputBase-input': {
            padding: '1em'
          },
          marginBottom: text === 'Question' ? '0em' : '1em'
        }}
      />
      {text === 'Question' && (
        <Grid sx={{ marginBottom: '1em' }}>
          <CustomSelect
            disabled={false}
            anchorEl={ownerAnchorEl}
            setAnchorEl={setOwnerAnchorEl}
            handleClose={handleOwnerSelect}
            options={owners}
            text="Select Users"
            show="ownerSelect"
            height="3.4em"
            selectWidth="100%"
            backgroundColor={Colors.NAV_BLACK}
          />
        </Grid>
      )}
      {mentions?.length > 0 && (
        <Grid container sx={{ marginBottom: '0.5em' }}>
          {mentions?.map((email, index) => (
            <Grid item key={index}>
              <Chip
                label={email}
                onDelete={() => setMentions((prev) => prev?.filter((item) => item !== email))}
                color="primary"
                variant="outlined"
                sx={{ marginBottom: '0.5em' }}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <div {...getRootProps({ style })}>
        <input {...getInputProps()} type="file" accept="application/pdf" />
        <Typography variant="body2" sx={{ color: Colors.WHITE }}>
          {commentsInput.ATTACHMENT_MESSAGE}
        </Typography>
      </div>
      {attachments?.length > 0 && (
        <Grid container direction="column" sx={{ marginTop: '1em' }}>
          {attachments?.map((file, index) => (
            <Grid
              key={index}
              container
              alignItems="center"
              sx={{
                backgroundColor: isLightTheme() ? Colors.BG_BLACK : Colors.NAV_BLACK,
                padding: '5px',
                borderRadius: '10px',
                marginBottom: '0.5em'
              }}>
              <AttachFile sx={{ color: Colors.WHITE, marginRight: '0.5em' }} />
              <Typography
                variant="body2"
                color="white"
                sx={{
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: '0.8rem'
                }}>
                {file.name}
              </Typography>
              <IconButton sx={{ color: 'red' }} onClick={() => handleDeleteAttachment(file.name)}>
                <DeleteOutline />
              </IconButton>
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container justifyContent="center">
        <Button
          type="submit"
          variant="outlined"
          color="primary"
          disabled={
            show === 'resolve'
              ? loading
              : loading || inputVal?.trim() === '' || (question && mentions?.length === 0)
          }
          sx={{
            marginTop: '1.5em',
            border: '1.5px solid',
            borderRadius: '10px',
            padding: '0.5em 2em',
            textTransform: 'none'
          }}>
          {loading ? 'Submitting...' : 'Submit'}
        </Button>
      </Grid>
    </form>
  );
};

export default CommentsInput;
